import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { colors } from '@sweep-io/sweep-design';
import { ConfigurationItemMap } from '../configuration-item-tables/shared/helpers';
import { ConfigurationType } from './types';
import uniq from 'lodash/uniq';
import { isItemActive } from '../helpers';
import { ConfigurationItem } from './DependenciesTypes';
import { OpenAiResponse, OpenAiDescription } from '../../parser/ParserTypes';
import { readableUUID } from '../../../navigation-utils/readableUUID';
import { getDataFromStepId } from '../pills/pillsUtils';

export const SectionBox = styled(Box)(() => ({
  padding: '0 0 16px 0',
  borderBottom: `1px solid ${colors.grey[120]}`,
  width: '100%',
}));

export const HEADER_TOP_ENDORNMENT = '28px';

// Memoization cache for expensive object transformations
const transformCache = new Map<string, any>();

const transformConfigItem = (item: ConfigurationItem) => {
  // Create a cache key based on item properties that matter for rendering
  const cacheKey = `${item.id}_${item.name}_${item.updatedAt}_${item.annotations?.changedAt}`;

  // Check cache first to avoid recreating objects
  if (transformCache.has(cacheKey)) {
    return transformCache.get(cacheKey);
  }

  // Create the transformed object
  const transformed = {
    id: item.id,
    lastModified: item.updatedAt ?? item?.annotations?.changedAt,
    label: item.label,
    type: item.type,
    parentType: item.parentType ?? '',
    link: item.link,
    filename: item.filename,
    flowType: item.flowType,
    objectName: item.objectName ?? item.objectApiNames?.[0] ?? '',
    name: item.name,
    isRollup: item.isRollup,
    //using isItemActive to assign isActive property as not all of the configuration types have it
    isActive: isItemActive(item),
  };

  // Cache the result
  transformCache.set(cacheKey, transformed);
  return transformed;
};

export const prepareConfigItemMapStructure = (items?: ConfigurationItemMap) => {
  if (!items) {
    return {} as ConfigurationItemMap;
  }

  const result = Object.keys(items).reduce((acc: any, key) => {
    const itemsOfKey = items[key as ConfigurationType];

    // Use cached transformations instead of creating new objects
    acc[key] = itemsOfKey.map(transformConfigItem);
    return acc as ConfigurationItemMap;
  }, {});

  return result;
};

export const SWEEP_ELEMENTS = [
  ConfigurationType.sweepAlert,
  ConfigurationType.sweepDedupe,
  ConfigurationType.sweepAutomation,
  ConfigurationType.sweepMatching,
  ConfigurationType.sweepAssignment,
  ConfigurationType.sweepScheduledAssignment,
  ConfigurationType.rollups,
];

export const excludedFromDetails = [
  ConfigurationType.apexClasses,
  ConfigurationType.apexTriggers,
  ConfigurationType.cpqData,
  ConfigurationType.objects,
  ConfigurationType.recordTypes,
  ...SWEEP_ELEMENTS.filter((item) => item !== ConfigurationType.rollups),
];

export const isFieldType = (itemType: string) =>
  itemType === ConfigurationType.fields || itemType === ConfigurationType.rollups;

export const formatOpenAiResponse = (res: OpenAiResponse): OpenAiDescription => {
  const isError = Boolean(res.error);
  return {
    type: isError ? 'error' : 'data',
    text: (res.data as string) || 'Could not load content at this time',
  };
};

export const getChips = (
  configurationItem: ConfigurationItem,
  stepNamesUsedInCanvas: string[],
  saltoObjectTypes: Pick<ObjectTypeName, 'objectType' | 'label'>[],
) => {
  const { objectApiNames, stagesNames, usedOnlyByRecordTypeLabels } = configurationItem ?? {};

  const objectLabels = objectApiNames?.map(
    (name) => saltoObjectTypes?.find((type) => type.objectType === name)?.label ?? name,
  );

  const stageNamesFromId = uniq(
    stagesNames
      ?.filter(
        (stageName) =>
          stageName &&
          stepNamesUsedInCanvas.some(
            (stepName) =>
              getDataFromStepId(stepName).stepFullName ===
              getDataFromStepId(stageName).stepFullName,
          ),
      )
      ?.map((name) => getDataFromStepId(name).stepFullName),
  );

  const stageNameChips = stageNamesFromId?.length ? stageNamesFromId : [];
  const objectChips = objectLabels && objectLabels?.length > 1 ? objectLabels : [];
  const RTchips = usedOnlyByRecordTypeLabels?.length ? uniq(usedOnlyByRecordTypeLabels) : [];
  const chips =
    stageNameChips.length || RTchips.length ? [...stageNameChips, ...RTchips] : [...objectChips];

  return { chips, RTchips };
};

export const buildConfigurationItemId = (item: {
  parentType: string;
  name: string;
  id?: string;
}) => {
  return (
    item.parentType +
    '|' +
    item.name +
    (item.id && readableUUID(item.id) ? '|' + readableUUID(item.id) : '')
  );
};

export const translateNewConfigurationTypeKeysToOldVersion = (type: string): ConfigurationType => {
  switch (type) {
    case 'apexClass':
    case 'ApexClass':
      return ConfigurationType.apexClasses;
    case 'emailTemplate':
    case 'EmailTemplate':
      return ConfigurationType.emailTemplates;
    case 'flow':
    case 'Flow':
      return ConfigurationType.flows;
    case 'processBuilderFlow':
    case 'ProcessBuilderFlow':
      return ConfigurationType.processBuilderFlows;
    case 'apexTrigger':
    case 'ApexTrigger':
      return ConfigurationType.apexTriggers;
    case 'approvalProcess':
    case 'ApprovalProcess':
      return ConfigurationType.approvalProcesses;
    case 'layout':
    case 'Layout':
      return ConfigurationType.layouts;
    case 'report':
    case 'Report':
      return ConfigurationType.reports;
    case 'validationRule':
    case 'ValidationRule':
      return ConfigurationType.validationRules;
    case 'workflowRule':
    case 'WorkflowRule':
      return ConfigurationType.workflowRules;
    default:
      return type as ConfigurationType;
  }
};
